import React, { useEffect, useState } from 'react';
import {
    Table,
    Button,
    Tag,
    Space,
    Modal,
    Input,
    message,
    Card,
    Typography,
    Badge,
    Descriptions,
} from 'antd';
import {
    CheckOutlined,
    CloseOutlined,
    DollarOutlined,
} from '@ant-design/icons';
import { expenseAPI } from '../../services/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { TextArea } = Input;

const FinanceExpensePage = () => {
    const [expenses, setExpenses] = useState([]);
    const [loading, setLoading] = useState(false);
    const [processModalVisible, setProcessModalVisible] = useState(false);
    const [selectedExpense, setSelectedExpense] = useState(null);
    const [rejectionReason, setRejectionReason] = useState('');
    const [isApproving, setIsApproving] = useState(true);

    useEffect(() => {
        fetchExpenses();
    }, []);

    const fetchExpenses = async () => {
        setLoading(true);
        try {
            const response = await expenseAPI.getAll({ status: 'finance_pending' });
            setExpenses(response.data.expenses);
        } catch (error) {
            message.error('경비 목록 조회 실패');
            console.error('Fetch expenses error:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleProcess = async (action) => {
        if (action === 'reject' && !rejectionReason.trim()) {
            message.error('반려 사유를 입력해주세요');
            return;
        }

        try {
            await expenseAPI.financeProcess(selectedExpense.id, {
                action,
                rejection_reason: action === 'reject' ? rejectionReason : undefined,
            });
            message.success(action === 'approve' ? '입금 처리되었습니다' : '경비가 반려되었습니다');
            setProcessModalVisible(false);
            setRejectionReason('');
            setSelectedExpense(null);
            fetchExpenses();
        } catch (error) {
            message.error(error.response?.data?.message || '처리 실패');
        }
    };

    const openProcessModal = (record, isApprove) => {
        setSelectedExpense(record);
        setIsApproving(isApprove);
        setProcessModalVisible(true);
    };

    const categoryMap = {
        meal: '식비',
        transport: '교통비',
        accommodation: '숙박비',
        supplies: '소모품',
        communication: '통신비',
        entertainment: '접대비',
        other: '기타',
    };

    const columns = [
        {
            title: 'PM승인일',
            dataIndex: 'pm_approved_at',
            key: 'pm_approved_at',
            width: 120,
            render: (date) => dayjs(date).format('MM-DD'),
        },
        {
            title: '지출일',
            dataIndex: 'expense_date',
            key: 'expense_date',
            width: 120,
            render: (date) => dayjs(date).format('YYYY-MM-DD'),
        },
        {
            title: '신청자',
            dataIndex: ['user', 'name'],
            key: 'user',
            width: 100,
            render: (name, record) => (
                <Space direction="vertical" size={0}>
                    <Text>{name}</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        {record.user?.employee_id}
                    </Text>
                </Space>
            ),
        },
        {
            title: '프로젝트',
            dataIndex: ['project', 'name'],
            key: 'project',
            ellipsis: true,
        },
        {
            title: '구분',
            dataIndex: 'category',
            key: 'category',
            width: 100,
            render: (category) => (
                <Tag color="blue">{categoryMap[category] || category}</Tag>
            ),
        },
        {
            title: '지출처',
            dataIndex: 'vendor',
            key: 'vendor',
            width: 150,
            ellipsis: true,
        },
        {
            title: '금액',
            dataIndex: 'amount',
            key: 'amount',
            width: 120,
            align: 'right',
            render: (amount) => (
                <Text strong style={{ color: '#cf1322' }}>
                    {parseInt(amount).toLocaleString()}원
                </Text>
            ),
        },
        {
            title: '계좌정보',
            key: 'bank',
            width: 200,
            ellipsis: true,
            render: (_, record) => (
                <Space direction="vertical" size={0}>
                    <Text>{record.bank_name}</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        {record.bank_account}
                    </Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        ({record.account_holder})
                    </Text>
                </Space>
            ),
        },
        {
            title: '작업',
            key: 'action',
            width: 150,
            fixed: 'right',
            render: (_, record) => (
                <Space>
                    <Button
                        type="primary"
                        size="small"
                        icon={<DollarOutlined />}
                        onClick={() => openProcessModal(record, true)}
                    >
                        입금
                    </Button>
                    <Button
                        danger
                        size="small"
                        icon={<CloseOutlined />}
                        onClick={() => openProcessModal(record, false)}
                    >
                        반려
                    </Button>
                </Space>
            ),
        },
    ];

    // 총 입금 예정 금액
    const totalAmount = expenses.reduce((sum, exp) => sum + parseFloat(exp.amount || 0), 0);

    return (
        <div>
            <Title level={2}>
                경비 입금 처리
                <Badge
                    count={expenses.length}
                    style={{ marginLeft: 16, backgroundColor: '#faad14' }}
                />
            </Title>

            <Card style={{ marginBottom: 24 }}>
                <Descriptions column={2}>
                    <Descriptions.Item label="입금 대기 건수">
                        <Text strong>{expenses.length}건</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="입금 예정 금액">
                        <Text strong style={{ fontSize: 18, color: '#cf1322' }}>
                            {totalAmount.toLocaleString()}원
                        </Text>
                    </Descriptions.Item>
                </Descriptions>
            </Card>

            <Card>
                <Table
                    columns={columns}
                    dataSource={expenses}
                    rowKey="id"
                    loading={loading}
                    pagination={{ pageSize: 10 }}
                    scroll={{ x: 1400 }}
                    locale={{ emptyText: '입금 대기 중인 경비가 없습니다' }}
                    expandable={{
                        expandedRowRender: (record) => (
                            <div style={{ padding: '12px 24px', background: '#fafafa' }}>
                                <Space direction="vertical" style={{ width: '100%' }}>
                                    <div>
                                        <Text strong>사용 목적:</Text>
                                        <div style={{ marginTop: 8 }}>{record.purpose}</div>
                                    </div>
                                    <div style={{ marginTop: 8 }}>
                                        <Text strong>PM:</Text>
                                        <span style={{ marginLeft: 8 }}>{record.pm?.name}</span>
                                    </div>
                                    <div>
                                        <Text strong>PM 승인일:</Text>
                                        <span style={{ marginLeft: 8 }}>
                      {dayjs(record.pm_approved_at).format('YYYY-MM-DD HH:mm')}
                    </span>
                                    </div>
                                </Space>
                            </div>
                        ),
                    }}
                />
            </Card>

            {/* 처리 모달 */}
            <Modal
                title={isApproving ? '입금 처리 확인' : '경비 반려'}
                open={processModalVisible}
                onOk={() => handleProcess(isApproving ? 'approve' : 'reject')}
                onCancel={() => {
                    setProcessModalVisible(false);
                    setRejectionReason('');
                    setSelectedExpense(null);
                }}
                okText={isApproving ? '입금 완료' : '반려'}
                cancelText="취소"
                okButtonProps={{ danger: !isApproving }}
            >
                <div style={{ marginBottom: 16 }}>
                    <Descriptions column={1} bordered size="small">
                        <Descriptions.Item label="신청자">
                            {selectedExpense?.user?.name} ({selectedExpense?.user?.employee_id})
                        </Descriptions.Item>
                        <Descriptions.Item label="금액">
                            <Text strong style={{ fontSize: 16, color: '#cf1322' }}>
                                {parseInt(selectedExpense?.amount || 0).toLocaleString()}원
                            </Text>
                        </Descriptions.Item>
                        <Descriptions.Item label="은행">
                            {selectedExpense?.bank_name}
                        </Descriptions.Item>
                        <Descriptions.Item label="계좌번호">
                            {selectedExpense?.bank_account}
                        </Descriptions.Item>
                        <Descriptions.Item label="예금주">
                            {selectedExpense?.account_holder}
                        </Descriptions.Item>
                        <Descriptions.Item label="지출처">
                            {selectedExpense?.vendor}
                        </Descriptions.Item>
                    </Descriptions>
                </div>

                {!isApproving && (
                    <TextArea
                        rows={4}
                        placeholder="반려 사유를 입력해주세요"
                        value={rejectionReason}
                        onChange={(e) => setRejectionReason(e.target.value)}
                    />
                )}

                {isApproving && (
                    <div style={{ marginTop: 16, padding: 12, background: '#fff7e6', borderRadius: 4 }}>
                        <Text type="warning">
                            위 계좌로 입금을 진행하시겠습니까? 입금 완료 후에는 프로젝트 경비 실적에 자동 반영됩니다.
                        </Text>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default FinanceExpensePage;